import React, { useState } from "react";
import { useTheme } from "styled-components";
import type { ResultItem } from "./types";
import { UnifiedAttributeChange } from "./Attributes";
import {
  GroupTitle,
  ChangeSection,
  ChangeSeparator,
  SubItemHeader,
  SubItemContent,
  CaretWrapper,
  ModifiedText,
  SubItemCard,
} from "./styles";
import { CaretDownIcon } from "../../../../components/Icons/Icons";
import { useTranslation } from "react-i18next";
import { LogTimeline } from "../../../../components/Logs/LogTimeline";
import { useRoutePath } from "../../../../util/Routing";

interface GroupItemProps {
  result: ResultItem;
  suppressHeader?: boolean;
}

export function GroupItem({ result, suppressHeader = false }: GroupItemProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const [isExpanded, setIsExpanded] = useState(false);

  if (result.message) {
    const isProductCreationRequest = result.id.includes("product-creation");
    const onToggle = () => {
      if (isProductCreationRequest) {
        window.open(
          `${adminPath}/pim/products/new/product-via-form/${result.targetName}`,
          "_blank"
        );
      }
    };
    return (
      <LogTimeline
        id={result.id}
        username={result.last_modified_by}
        date={result.modified_at}
        message={<>{result.message}</>}
        isExpanded={false}
        onToggle={onToggle}
        showToggleButton={isProductCreationRequest}
        buttonText={isProductCreationRequest ? t("View Form") : undefined}
      />
    );
  }

  if (suppressHeader) {
    return (
      <SubItemCard>
        <SubItemHeader onClick={() => setIsExpanded(!isExpanded)}>
          <CaretWrapper isExpanded={isExpanded}>
            <CaretDownIcon
              height={16}
              width={16}
              fill={theme.secondaryTextColor}
            />
          </CaretWrapper>
          <ModifiedText>
            <b>{result.targetName}</b>
          </ModifiedText>
        </SubItemHeader>
        {isExpanded && (
          <SubItemContent>
            {result.subItems.map((subItem, index) => {
              const isLastItem = index === result.subItems.length - 1;

              return (
                <React.Fragment
                  key={`${result.id}-${subItem.attributeName}-${index}`}
                >
                  <ChangeSection>
                    <UnifiedAttributeChange subItem={subItem} />
                  </ChangeSection>
                  {!isLastItem && <ChangeSeparator />}
                </React.Fragment>
              );
            })}
          </SubItemContent>
        )}
      </SubItemCard>
    );
  }

  const children = (
    <>
      <GroupTitle>
        <b>{result.targetName}</b>
      </GroupTitle>
      {result.subItems.map((subItem, index) => {
        const isLastItem = index === result.subItems.length - 1;

        return (
          <React.Fragment
            key={`${result.id}-${subItem.attributeName}-${index}`}
          >
            <ChangeSection>
              <UnifiedAttributeChange subItem={subItem} />
            </ChangeSection>
            {!isLastItem && <ChangeSeparator />}
          </React.Fragment>
        );
      })}
    </>
  );

  const message = (
    <>
      {suppressHeader ||
      result.id.includes("asset-") ||
      result.id.includes("external-link")
        ? t(result.operation)
        : t("Modified")}{" "}
      <b>{result.targetName}</b>{" "}
    </>
  );

  return (
    <LogTimeline
      id={result.id}
      username={result.last_modified_by}
      date={result.modified_at}
      message={message}
      isExpanded={isExpanded}
      children={children}
      onToggle={() => setIsExpanded(!isExpanded)}
    />
  );
}
